/**
 * The audit viewer (P4-BR2, §12.4).
 *
 * "Transparency is the feature" is the whole design brief. There is no filter
 * limiting a partner to their own actions, no permission gate, and no edit
 * control anywhere on this screen — the server has no write route to call.
 *
 * The sentences and the field-by-field changes both arrive pre-phrased from
 * the server in the partner's language (P4-TR4). The browser never sees the
 * raw before/after JSON, which keeps the redaction problem in one place
 * rather than two.
 *
 * A row collapses to one line and opens to the whole story: what each field
 * was and became, the moment to the second, the record it touched, and the
 * request and address it came from. The summary alone was too little — a
 * change touching eight fields arrived as a paragraph of semicolons — and the
 * raw JSON would be too much. This is the middle: a sentence to scan, a table
 * to check.
 *
 * Table names come from the server rather than a local i18n key. The client
 * used to keep its own catalogue and the two drifted, so entries for tables
 * the client had never heard of rendered as the literal missing key.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { useAuditFeed, useAuditHistory, useAuditTables, type AuditEntry } from './api.js';

/** "2026-09-19, 15:35" — the list needs the minute (AP-7, NFR-9). */
const atInDhaka = (iso: string): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Dhaka',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));

/**
 * The same instant to the second, for the opened row.
 *
 * "Which of these two edits came first" is a question this screen exists to
 * answer, and two changes a few seconds apart are indistinguishable without
 * it. Kept out of the collapsed list, where it would only add width.
 */
const preciseInDhaka = (iso: string): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Dhaka',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(iso));

const ACTION_TONE: Record<string, string> = {
  create: 'bg-green-50 text-green-800',
  approve: 'bg-green-50 text-green-800',
  reject: 'bg-red-50 text-red-800',
  void: 'bg-red-50 text-red-800',
  update: 'bg-gray-100 text-gray-700',
};

const keyOf = (entry: AuditEntry): string => `${entry.table}-${String(entry.recordId)}-${entry.at}-${entry.action}`;

function ActionBadge({ action }: { action: string }): JSX.Element {
  const { t } = useTranslation();

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${ACTION_TONE[action] ?? 'bg-gray-100 text-gray-700'}`}>
      {t(`audit.action.${action}`)}
    </span>
  );
}

/** What each field was and became. The part the old screen never showed. */
function ChangeTable({ entry }: { entry: AuditEntry }): JSX.Element {
  const { t } = useTranslation();

  if (entry.changes.length === 0) {
    return <p className="text-xs text-gray-500">{t('audit.noFieldChanges')}</p>;
  }

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-500">
          <th className="py-1 pr-3 font-medium">{t('audit.field')}</th>
          <th className="py-1 pr-3 font-medium">{t('audit.before')}</th>
          <th className="py-1 font-medium">{t('audit.after')}</th>
        </tr>
      </thead>
      <tbody>
        {entry.changes.map((change) => (
          <tr key={change.field} className="border-t border-gray-100 align-top">
            <td className="py-1 pr-3 text-gray-700">
              {change.label}
              {/* The column name beside the label: a partner comparing with a
                  developer needs both halves of that conversation. */}
              {change.label !== change.field && <span className="ml-1 text-[10px] text-gray-400">{change.field}</span>}
            </td>
            <td className="py-1 pr-3 text-red-800 line-through decoration-red-300">{change.from}</td>
            <td className="py-1 font-medium text-green-800">{change.to}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Every other change ever made to the same record (§12.4). */
function RecordHistory({ entry }: { entry: AuditEntry }): JSX.Element {
  const { t } = useTranslation();
  const { data: history, isLoading } = useAuditHistory(entry.table, entry.recordId);

  if (isLoading) return <p className="text-xs text-gray-500">{t('app.loading')}</p>;

  const others = (history ?? []).filter((row) => row.at !== entry.at || row.action !== entry.action);
  if (others.length === 0) return <p className="text-xs text-gray-500">{t('audit.onlyChange')}</p>;

  return (
    <ol className="space-y-1">
      {others.map((row) => (
        <li key={keyOf(row)} className="flex flex-wrap items-baseline gap-2 text-xs text-gray-600">
          <span className="tabular-nums text-gray-500">{preciseInDhaka(row.at)}</span>
          <ActionBadge action={row.action} />
          <span>{row.actorName ?? t('audit.system')}</span>
          <span className="text-gray-700">{row.summary}</span>
        </li>
      ))}
    </ol>
  );
}

/** The opened row: the change, the provenance, and the record's other changes. */
function EntryDetail({ entry }: { entry: AuditEntry }): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="space-y-3 border-t border-gray-100 bg-gray-50 px-4 py-3">
      <ChangeTable entry={entry} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
        {[
          ['audit.exactTime', preciseInDhaka(entry.at)],
          ['audit.record', `${entry.tableLabel} #${String(entry.recordId)}`],
          // Stored on every row since P0 and never shown. The request id is
          // what ties an entry to the server logs when something needs
          // investigating; the address says which device it came from.
          ['audit.requestId', entry.requestId ?? '—'],
          ['audit.ip', entry.ip ?? '—'],
        ].map(([labelKey, value]) => (
          <div key={labelKey}>
            <dt className="text-gray-500">{t(labelKey!)}</dt>
            <dd className="break-all font-medium text-gray-800">{value}</dd>
          </div>
        ))}
      </dl>

      <div>
        <p className="mb-1 text-xs font-medium text-gray-500">{t('audit.recordHistory')}</p>
        <RecordHistory entry={entry} />
      </div>
    </div>
  );
}

/** One event on a phone. */
function EntryCard({ entry }: { entry: AuditEntry }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <li className="overflow-hidden rounded-xl bg-white shadow-sm">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="w-full px-4 py-3 text-left">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-gray-900">{entry.summary}</p>
          <span className="shrink-0">
            <ActionBadge action={entry.action} />
          </span>
        </div>

        <p className="mt-1 text-xs text-gray-500">
          {entry.actorName ?? t('audit.system')} · {atInDhaka(entry.at)} · {entry.tableLabel} #{entry.recordId}
        </p>
      </button>

      {open && <EntryDetail entry={entry} />}
    </li>
  );
}

export function AuditViewer(): JSX.Element {
  const { t } = useTranslation();
  const [table, setTable] = useState<string>('');
  const [openKey, setOpenKey] = useState<string | null>(null);

  const { data: entries, isLoading, isError, refetch } = useAuditFeed(table === '' ? undefined : table);
  const { data: tables = [] } = useAuditTables();

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('audit.title')}</h1>

      <p className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">{t('audit.explainer')}</p>

      <label className="block text-xs text-gray-500" htmlFor="audit-table">
        {t('audit.filter')}
      </label>
      <select
        id="audit-table"
        value={table}
        onChange={(event) => setTable(event.target.value)}
        className="mb-4 mt-1 min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      >
        <option value="">{t('audit.allActivity')}</option>
        {/* Every table the server audits, labelled by the server. The client
            used to list seven of twenty-eight from its own hardcoded copy. */}
        {tables.map((option) => (
          <option key={option.name} value={option.name}>
            {option.label}
          </option>
        ))}
      </select>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {entries !== undefined && entries.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500">{t('audit.empty')}</p>
      )}

      {/* Phone: one card per event. */}
      <ul className="space-y-2 md:hidden">
        {(entries ?? []).map((entry) => (
          <EntryCard key={keyOf(entry)} entry={entry} />
        ))}
      </ul>

      {/* Desktop: when · who · what, which is how an audit trail is read. */}
      {entries !== undefined && entries.length > 0 && (
        <div className="hidden overflow-x-auto rounded-xl bg-white shadow-sm md:block">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-40">{t('audit.when')}</th>
                <th className="w-40">{t('audit.who')}</th>
                <th className="w-28">{t('audit.actionLabel')}</th>
                <th>{t('audit.what')}</th>
                <th className="w-44">{t('audit.record')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const key = keyOf(entry);
                const open = openKey === key;

                return [
                  <tr
                    key={key}
                    onClick={() => setOpenKey(open ? null : key)}
                    aria-expanded={open}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap text-gray-500">{atInDhaka(entry.at)}</td>
                    <td>{entry.actorName ?? t('audit.system')}</td>
                    <td>
                      <ActionBadge action={entry.action} />
                    </td>
                    <td>
                      <span aria-hidden="true" className="mr-1 text-gray-400">
                        {open ? '▾' : '▸'}
                      </span>
                      {entry.summary}
                    </td>
                    <td className="whitespace-nowrap text-gray-500">
                      {entry.tableLabel} #{entry.recordId}
                    </td>
                  </tr>,
                  open ? (
                    <tr key={`${key}-detail`}>
                      <td colSpan={5} className="p-0">
                        <EntryDetail entry={entry} />
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
